"use client";

import { useCallback, useMemo, useTransition } from "react";
import { useRouter } from "next/navigation";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

import { MusicCategoryPageClientV2, type MusicCategoryPageClientV2Props } from "./MusicCategoryPageClientV2";

type MusicCategoryPageNavigatorProps = Omit<
  MusicCategoryPageClientV2Props,
  "onCategorySelect" | "onCategorySearch" | "isPending"
>;

/** Thin wrapper that injects Next.js router navigation and category search into `MusicCategoryPageClientV2`. */
export function MusicCategoryPageNavigator(props: MusicCategoryPageNavigatorProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const handleCategorySelect = useCallback(
    (slug: string) => {
      startTransition(() => {
        router.push(`/music/${slug}`);
      });
    },
    [router],
  );

  const handleCategorySearch = useCallback(
    async (query: string, signal: AbortSignal) => {
      const params = new URLSearchParams({ q: query, locale: props.locale });
      const res = await fetch(`/next/api/music-categories/search?${params.toString()}`, {
        signal,
      });
      return res.json();
    },
    [props.locale],
  );

  // Inject onSelect into href-based pills so SearchInput can intercept normal left-clicks
  // and route through startTransition (for the fade). Modifier clicks (cmd, middle-click)
  // fall through to the href and open a new tab as expected.
  const navigableSuggestions = useMemo(
    () =>
      props.searchSuggestions.map(s => {
        if (!s.href) return s;
        const href = s.href;
        return {
          ...s,
          onSelect: () => {
            captureEvent(
              EventName.mw_music_category_suggestion_clicked,
              {
                slug: href.split("/").pop() ?? "",
                label: s.label,
              },
              { transport: "sendBeacon" },
            );
            startTransition(() => router.push(href));
          },
        };
      }),

    [props.searchSuggestions, router],
  );

  return (
    <MusicCategoryPageClientV2
      {...props}
      searchSuggestions={navigableSuggestions}
      isPending={isPending}
      onCategorySelect={handleCategorySelect}
      onCategorySearch={handleCategorySearch}
    />
  );
}
